"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";

import { useAccount } from "@/hooks/useAccount";
import { getMediaCdnUrl } from "@/lib/cdn";
import { useAuth } from "@/providers/auth.provider";
import { cn } from "@/lib/utils";
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gogive/design-system";
import { Icon } from "@gogive/design-system/icons";
import {
  SidebarHeaderMenuButton,
} from "@gogive/design-system/components/sidebar";
import { type ReactNode } from "react";

function UserAvatar({ size = "8" as const }: { size?: "8" | "10" }) {
  const { user } = useAuth();
  const initials = useMemo(() => {
    if (!user) return "?";
    const parts = [user.firstName, user.lastName]
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase());
    return parts.join("") || "?";
  }, [user]);

  return (
    <Avatar size={size} variant="user">
      <AvatarFallback className="bg-muted text-xs font-medium text-foreground">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

function CharityBrandMark({
  className,
  loading,
}: {
  className?: string;
  loading?: boolean;
}) {
  const { account } = useAccount();
  const logoUrl = account?.branding?.logoUrl ?? undefined;
  const name = account?.name ?? "Your charity";
  const logo = useMemo(
    () => (!account ? undefined : getMediaCdnUrl(account.id, "logo", logoUrl)),
    [account, logoUrl],
  );
  const fallbackInitial = name.charAt(0).toUpperCase() || "?";

  if (loading) {
    return (
      <span
        aria-hidden
        className={cn(
          "size-6 shrink-0 animate-pulse rounded-md bg-muted",
          className,
        )}
      />
    );
  }

  if (logo) {
    return (
      <Image
        src={logo}
        alt=""
        width={24}
        height={24}
        className={cn("size-6 shrink-0 rounded-md object-contain", className)}
        unoptimized
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground",
        className,
      )}
    >
      {fallbackInitial}
    </span>
  );
}

export function UserMenu({
  trigger,
  className,
  placement = "avatar",
}: {
  trigger?: ReactNode;
  className?: string;
  /** Atlas sidebar header: charity name opens the account menu. */
  placement?: "avatar" | "sidebar";
} = {}) {
  const { logout, user } = useAuth();
  const { account, isLoading: accountLoading } = useAccount();
  const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const charityName = account?.name;
  const isAdmin = useMemo(
    () => Boolean(user && ["SUPER_ADMIN", "ADMIN"].includes(user.role)),
    [user],
  );

  const defaultTrigger =
    placement === "sidebar" ? (
      <SidebarHeaderMenuButton
        emphasis
        className="h-9 w-full min-w-0 px-1.5"
        type="button"
      >
        <CharityBrandMark loading={accountLoading} />
        {accountLoading ? (
          <span
            aria-hidden
            className="h-4 min-w-0 flex-1 animate-pulse rounded-md bg-muted"
          />
        ) : (
          <span className="truncate">{charityName ?? "Your charity"}</span>
        )}
        <Icon src="ChevronDown" className="ml-auto size-4 shrink-0 opacity-50" />
      </SidebarHeaderMenuButton>
    ) : (
      <button
        type="button"
        className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <UserAvatar />
      </button>
    );

  return (
    <div
      className={cn(
        "flex items-center",
        placement === "sidebar" && "w-full min-w-0",
        className,
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger ?? defaultTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={placement === "sidebar" ? "start" : "end"}
          side={placement === "sidebar" ? "right" : "bottom"}
          className="w-72"
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-3 px-2 py-2.5">
              <UserAvatar size="10" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium leading-none">
                  {fullName || "Account"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild icon="Settings">
              <Link href="/settings/brand">Settings</Link>
            </DropdownMenuItem>
            {isAdmin ? (
              <DropdownMenuItem asChild icon="UserPlus">
                <Link href="/settings/team">Invite new members</Link>
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild icon="CircleQuestionMark">
            <a href="mailto:admin@gogive.com.au">Contact us</a>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            icon="LogOut"
            onSelect={() => logout()}
          >
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
